import React, { useState } from 'react';
import { authAPI } from '../utils/api';
import { toast } from 'sonner';
import { Store, X } from 'lucide-react';

/**
 * BusinessNameModal — pops after a successful dealer-plan purchase.
 * Asks the user for their Business Name and saves it to user.business_name.
 *
 * Props:
 *   open       — boolean: show/hide
 *   onClose    — invoked after save (or skip) so caller can navigate / refresh
 *   categoryLabel — e.g. "Mobiles", "Bikes", "Cars", "Property" (display only)
 *   collectAlternatePhone — when true, shows an optional Alternate Mobile Number
 *     field (used by Property Dealer plans per iter56).
 */
const BusinessNameModal = ({ open, onClose, categoryLabel = '', collectAlternatePhone = false }) => {
  const [name, setName] = useState('');
  const [altPhone, setAltPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const handleSave = async () => {
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      toast.error('Business name must be at least 2 characters');
      return;
    }
    const altDigits = (altPhone || '').replace(/\D+/g, '');
    if (collectAlternatePhone && altDigits && (altDigits.length < 7 || altDigits.length > 15)) {
      toast.error('Alternate phone must be 7-15 digits');
      return;
    }
    setSubmitting(true);
    try {
      await authAPI.updateBusinessName(trimmed, collectAlternatePhone ? (altDigits || null) : undefined);
      toast.success('Business name saved!');
      onClose?.(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save business name');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" data-testid="business-name-modal">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 relative">
        <button
          data-testid="business-name-modal-close"
          onClick={() => onClose?.(false)}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-2xl bg-[#00bf63]/10 flex items-center justify-center flex-shrink-0">
            <Store size={24} className="text-[#00bf63]" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[#0A0B10]" style={{ fontFamily: 'Google Sans Flex' }}>
              Welcome, Dealer!
            </h2>
            {categoryLabel ? (
              <p className="text-xs text-[#4B5563]">{categoryLabel} Dealer Plan activated</p>
            ) : null}
          </div>
        </div>

        <p className="text-sm text-[#4B5563] mb-5">
          Tell us your business name. It will appear with a verified <span className="font-bold text-[#00bf63]">DEALER</span> badge on your listings so buyers know they&apos;re talking to a registered dealer.
        </p>

        <label className="block text-sm font-semibold text-[#0A0B10] mb-2">Business Name</label>
        <input
          data-testid="business-name-input"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Sharma Mobile Hub"
          autoFocus
          maxLength={60}
          className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#00bf63] focus:outline-none transition-colors text-[#0A0B10]"
        />
        <p className="text-xs text-gray-400 mt-1">{name.length}/60</p>

        {collectAlternatePhone && (
          <div className="mt-4">
            <label className="block text-sm font-semibold text-[#0A0B10] mb-2">
              Alternate Mobile Number <span className="text-xs font-normal text-gray-400">(optional)</span>
            </label>
            <input
              data-testid="alternate-phone-input"
              type="tel"
              value={altPhone}
              onChange={(e) => setAltPhone(e.target.value)}
              placeholder="e.g. +91 98765 43210"
              maxLength={20}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#00bf63] focus:outline-none transition-colors text-[#0A0B10]"
            />
            <p className="text-xs text-gray-400 mt-1">Used by Admin for dealer support only. Not shown publicly.</p>
          </div>
        )}

        <div className="flex gap-3 mt-6">
          <button
            data-testid="business-name-skip"
            onClick={() => onClose?.(false)}
            disabled={submitting}
            className="flex-1 px-5 py-3 rounded-full border-2 border-gray-200 text-[#4B5563] font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Skip for now
          </button>
          <button
            data-testid="business-name-save"
            onClick={handleSave}
            disabled={submitting || name.trim().length < 2}
            className="flex-1 px-5 py-3 rounded-full bg-[#00bf63] text-white font-bold hover:bg-[#00a554] transition-colors disabled:opacity-50"
          >
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </div>

        <p className="text-xs text-gray-400 mt-4 text-center">
          You can edit this anytime from My Account.
        </p>
      </div>
    </div>
  );
};

export default BusinessNameModal;
